import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiSliders, FiX } from "react-icons/fi";
import productData, { categories } from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

const colorFilters = ["Green", "Blue", "Red", "Brown", "Black", "Cream"];
const sizeFilters = ["XS", "S", "M", "L", "32", "36", "38", "40", "OS"];

const swatchColor = (color) => {
  const value = color.toLowerCase();
  if (value.includes("green")) return "#245742";
  if (value.includes("blue") || value.includes("cobalt")) return "#1749b8";
  if (value.includes("red") || value.includes("ruby")) return "#c61f31";
  if (value.includes("brown") || value.includes("camel")) return "#77513c";
  if (value.includes("black") || value.includes("midnight")) return "#171717";
  if (value.includes("cream") || value.includes("ivory")) return "#e9e2d4";
  return "linear-gradient(135deg,#e15c76 0 50%,#6f358c 50%)";
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { wishlistItems, toggleWishlist } = useContext(CartContext);
  const [filterOpen, setFilterOpen] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const query = searchParams.get("q")?.trim().toLowerCase() || "";
  const selectedCategory = searchParams.get("category") || "All";
  const sort = searchParams.get("sort") || "suggested";
  const wishlistOnly = searchParams.get("wishlist") === "true";

  useEffect(() => {
    document.title = wishlistOnly
      ? `Wishlist | ${siteConfig.displayName}`
      : `New in | ${siteConfig.displayName}`;
  }, [wishlistOnly]);

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [query, selectedCategory, sort, selectedColors, selectedSizes, wishlistOnly]);

  useEffect(() => {
    document.body.classList.toggle("nav-open", filterOpen);
    return () => document.body.classList.remove("nav-open");
  }, [filterOpen]);

  const filteredProducts = useMemo(() => {
    const products = productData.filter((product) => {
      const searchText = [
        product.name,
        product.category,
        product.material,
        ...product.tags,
      ]
        .join(" ")
        .toLowerCase();
      const matchesQuery = !query || searchText.includes(query);
      const matchesCategory =
        selectedCategory === "All" || product.category === selectedCategory;
      const matchesColor =
        selectedColors.length === 0 ||
        selectedColors.some((color) =>
          product.colors.some((item) =>
            item.toLowerCase().includes(color.toLowerCase())
          )
        );
      const matchesSize =
        selectedSizes.length === 0 ||
        selectedSizes.some((size) => product.size.includes(size));
      const matchesWishlist = !wishlistOnly || wishlistItems.includes(product.id);
      const matchesNew = sort !== "new" || product.isNew;
      return (
        matchesQuery &&
        matchesCategory &&
        matchesColor &&
        matchesSize &&
        matchesWishlist &&
        matchesNew
      );
    });

    return [...products].sort((a, b) => {
      if (sort === "price-asc") return Number(a.price) - Number(b.price);
      if (sort === "price-desc") return Number(b.price) - Number(a.price);
      if (sort === "name") return a.name.localeCompare(b.name);
      return a.id - b.id;
    });
  }, [query, selectedCategory, selectedColors, selectedSizes, sort, wishlistOnly, wishlistItems]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All" || value === "suggested") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const toggleValue = (setter, value) =>
    setter((items) =>
      items.includes(value) ? items.filter((item) => item !== value) : [...items, value]
    );

  const clearFilters = () => {
    setSelectedColors([]);
    setSelectedSizes([]);
    setSearchParams({});
  };

  const activeFilterCount =
    selectedColors.length +
    selectedSizes.length +
    (selectedCategory !== "All" ? 1 : 0) +
    (wishlistOnly ? 1 : 0);

  return (
    <main className="shop-page">
      <header className="shop-heading">
        <h1>{wishlistOnly ? "Wishlist" : query ? `Search: “${query}”` : "New in"}</h1>
        <div className={`shop-heading__copy ${descriptionOpen ? "is-open" : ""}`}>
          <p>
            Discover your next wardrobe treasures among our new arrivals — from
            feel-good dresses and expressive knitwear to bright accessories made
            for the season ahead.
          </p>
        </div>
        <button
          className="description-toggle"
          onClick={() => setDescriptionOpen((open) => !open)}
          aria-label={descriptionOpen ? "Collapse description" : "Expand description"}
        >
          <FiChevronDown className={descriptionOpen ? "is-open" : ""} />
        </button>
      </header>

      <div className="catalog-toolbar">
        <span>{filteredProducts.length} items</span>
        <div className="catalog-toolbar__actions">
          <button onClick={() => setFilterOpen(true)}>
            <FiSliders /> Filter{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </button>
          <label>
            <span className="sr-only">Sort products</span>
            <select value={sort} onChange={(event) => updateParam("sort", event.target.value)}>
              <option value="suggested">Sort: Suggested</option>
              <option value="new">Sort: New arrivals</option>
              <option value="price-asc">Price ascending</option>
              <option value="price-desc">Price descending</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
      </div>

      {activeFilterCount > 0 && (
        <div className="active-filters">
          {selectedCategory !== "All" && (
            <button onClick={() => updateParam("category", "All")}>
              {selectedCategory} <FiX />
            </button>
          )}
          {selectedColors.map((color) => (
            <button key={color} onClick={() => toggleValue(setSelectedColors, color)}>
              {color} <FiX />
            </button>
          ))}
          {selectedSizes.map((size) => (
            <button key={size} onClick={() => toggleValue(setSelectedSizes, size)}>
              Size {size} <FiX />
            </button>
          ))}
          <button className="clear-filter" onClick={clearFilters}>Clear all</button>
        </div>
      )}

      {isLoading ? (
        <div className="product-grid product-grid--loading" aria-label="Loading products">
          {Array.from({ length: 8 }).map((_, index) => (
            <div className="product-skeleton" key={index} />
          ))}
        </div>
      ) : filteredProducts.length ? (
        <div className="product-grid" aria-live="polite">
          {filteredProducts.map((product) => (
            <article className="product-card" key={product.id}>
              <Link className="product-card__media" to={`/shopDetails/${product.id}`}>
                <img
                  className="product-card__image product-card__image--primary"
                  src={product.images[0]}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                />
                <img
                  className="product-card__image product-card__image--alternate"
                  src={product.images[1]}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                {product.isNew && <span className="product-badge">New</span>}
              </Link>
              <button
                className={`wishlist-button ${
                  wishlistItems.includes(product.id) ? "is-active" : ""
                }`}
                onClick={() => toggleWishlist(product.id)}
                aria-label={`${
                  wishlistItems.includes(product.id) ? "Remove" : "Add"
                } ${product.name} ${
                  wishlistItems.includes(product.id) ? "from" : "to"
                } wishlist`}
              >
                <FiHeart />
              </button>
              <Link className="product-card__info" to={`/shopDetails/${product.id}`}>
                <strong title={product.name}>{product.name}</strong>
                <span>{formatPrice(product.price)}</span>
                <div className="product-swatches" aria-label="Available colors">
                  {product.colors.map((color) => (
                    <i
                      key={color}
                      style={{ background: swatchColor(color) }}
                      title={color}
                    />
                  ))}
                </div>
              </Link>
            </article>
          ))}
        </div>
      ) : (
        <section className="catalog-empty">
          <h2>No pieces found</h2>
          <p>Try removing a filter or exploring the complete collection.</p>
          <button className="button" onClick={clearFilters}>View all products</button>
        </section>
      )}

      {!isLoading && filteredProducts.length > 0 && (
        <div className="catalog-count">
          <span>Showing {filteredProducts.length} out of {productData.length} products</span>
          <Link className="button button--outline" to="/shop">Show all</Link>
        </div>
      )}

      <div
        className={`filter-overlay ${filterOpen ? "is-open" : ""}`}
        onClick={() => setFilterOpen(false)}
        aria-hidden="true"
      />
      <aside className={`filter-drawer ${filterOpen ? "is-open" : ""}`}>
        <header>
          <h2>Filter</h2>
          <button onClick={() => setFilterOpen(false)} aria-label="Close filters"><FiX /></button>
        </header>
        <div className="filter-drawer__content">
          <fieldset>
            <legend>Category</legend>
            {categories.map((category) => (
              <label key={category}>
                <input
                  type="radio"
                  name="category"
                  checked={selectedCategory === category}
                  onChange={() => updateParam("category", category)}
                />
                <span>{category}</span>
                <small>
                  {category === "All"
                    ? productData.length
                    : productData.filter((product) => product.category === category).length}
                </small>
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Color</legend>
            {colorFilters.map((color) => (
              <label key={color}>
                <input
                  type="checkbox"
                  checked={selectedColors.includes(color)}
                  onChange={() => toggleValue(setSelectedColors, color)}
                />
                <span>{color}</span>
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Size</legend>
            <div className="size-filter-grid">
              {sizeFilters.map((size) => (
                <button
                  key={size}
                  className={selectedSizes.includes(size) ? "is-selected" : ""}
                  onClick={() => toggleValue(setSelectedSizes, size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
        <footer>
          <button className="filter-clear" onClick={clearFilters}>Clear</button>
          <button className="button" onClick={() => setFilterOpen(false)}>
            Show {filteredProducts.length} results
          </button>
        </footer>
      </aside>
    </main>
  );
}

export default Shop;
